import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ɵFormGroupRawValue, ɵFormGroupValue, ɵTypedOrUntyped} from '@angular/forms';
import {MatChipSelectionChange} from '@angular/material/chips';
import {IncidentFilterRole, ReferenceBookResponse, SubjectType} from '@npck/af-frontend-api';
import {DATE_SERVER_FORMAT} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {debounceTime, distinctUntilChanged, Observable} from 'rxjs';
import {
    ExportFraudFileDialogData,
    ExportSubjectFileDialogComponent,
    ImportExportMode,
} from '../../../pages/subjects/export-subject-file-dialog/export-subject-file-dialog.component';
import {
    ImportFraudFileDialogData,
    ImportSubjectFileDialogComponent,
} from '../../../pages/subjects/import-subject-file-dialog/import-subject-file-dialog.component';
import {DateForm} from '../../date-picker-field/date-picker-field.component';
import {DateRangeForm} from '../../date-range-picker-field/date-range-picker-field.component';
import {FilterDialogComponent, FilterDialogData} from './filter-dialog/filter-dialog.component';

@Component({
    selector: 'af-filter',
    templateUrl: 'filter.component.html',
    styleUrls: ['filter.component.scss'],
    standalone: false,
})
export class FilterComponent implements OnInit, OnChanges {
    @Input() filterFields: FilterField[] = [{
        name: 'query',
        type: 'text',
    }];
    @Input() chips: string[] = [];
    @Input() hasContent: boolean = false;
    @Input() filterEvent: FilterEvent;
    @Input() placeholderContext: SearchPlaceholderContext;
    @Input() exportImportMode: ImportExportMode;
    @Input() chipsFormControlName: keyof FilterEventForm;
    @Input() showSearchBar: boolean = true;
    @Input() showImport: boolean;
    @Input() showExport: boolean;
    @Input() allowSearchSpaces: boolean = true;
    @Output() filterListener = new EventEmitter<FilterEvent>();
    form: FormGroup<FilterEventForm>;
    isFilterSelected = false;

    constructor(
        private formBuilder: FormBuilder,
        private dialogService: DialogService,
    ) {
    }

    get hasFilterFields(): boolean {
        return this.filterFields.some(field => field.name != 'query');
    }

    ngOnInit(): void {
        if (this.chips?.length && !this.chipsFormControlName) {
            console.error('[FilterComponent] chips provided but chipsFormControlName is not set', {
                chips: this.chips,
                chipsFormControlName: this.chipsFormControlName,
            });
        }
        this.buildForm();
        if (this.filterEvent) {
            this.form.patchValue(this.filterEvent, {emitEvent: false});
        }
        this.updateFilterToggleState(this.filterEvent);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.filterEvent && !changes.filterEvent.firstChange) {
            this.updateFilterToggleState(this.filterEvent);
        }
    }

    openFilters(): void {
        this.dialogService.openCustomDialog<FilterDialogComponent, FilterEvent>(FilterDialogComponent, <FilterDialogData>{
            fields: this.filterFields.filter(field => field.name != 'query'),
            filter: this.filterEvent || this.form.value,
        }, {
            panelClass: 'filter-dialog',
            enterAnimationDuration: 0,
            position: {right: '0'},
            exitAnimationDuration: 0,
            autoFocus: false,
        })
            .subscribe(result => {
                if (result) {
                    this.emitValue({...this.form.value, ...result});
                    this.updateFilterToggleState(result);
                }
            });
    }

    subscribeToFormChanges(): void {
        this.form.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
            )
            .subscribe(result => {
                this.emitValue({
                    ...this.filterEvent,
                    query: result.query?.trim() || null,
                    [this.chipsFormControlName]: result[this.chipsFormControlName],
                });
            });
    }

    onChipSelectionChange(event: MatChipSelectionChange, chip: string): void {
        const controls = (this.form.get(this.chipsFormControlName) as FormGroup<DynamicBooleanForm>).controls;
        Object.keys(controls).forEach(key => controls[key].reset());
        controls[chip].setValue(event.selected);
    }

    onSearchKeydown(event: KeyboardEvent): void {
        if (!this.allowSearchSpaces && event.key === ' ') {
            event.preventDefault();
        }
    }

    clearSearch(): void {
        this.form.controls.query.reset();
    }

    reset(): void {
        this.form.reset(undefined, {emitEvent: false});
        this.isFilterSelected = false;
    }

    importFraudList(): void {
        const subjectTypeValues = fieldExtractor(this.filterEvent.subjectType);
        const subjectType = subjectTypeValues?.length && subjectTypeValues[0] as SubjectType;
        this.dialogService.openCustomDialogFitContent(ImportSubjectFileDialogComponent, <ImportFraudFileDialogData>{
            importMode: this.exportImportMode,
            subjectType: subjectType,
        }, {autoFocus: false});
    }

    exportFraudList(): void {
        this.dialogService.openCustomDialogFitContent(ExportSubjectFileDialogComponent, <ExportFraudFileDialogData>{
            exportMode: this.exportImportMode,
            filter: this.form.value,
        }, {autoFocus: false});
    }

    private emitValue(filter: FilterEvent): void {
        this.filterListener.emit(filter);
    }

    private buildForm(): void {
        this.form = this.formBuilder.group({});
        const simpleControlNames: (keyof FilterEventForm)[] =
            ['deleted', 'incidentRole', 'incidentSourceAuthors', 'query', 'rrn', 'refundAuthor'];
        const groupControlNames: (keyof FilterEventForm)[] = [
            'status',
            'subjectType',
            'incidentType',
            'incidentPriority',
            'incidentResolution',
            'incidentStatus',
            'sourceOrganizations',
            'incidentGovernment',
            'refundRelatedOrganizations',
            'refundGovernment',
            'incidentFraudTypes',
            'incidentTransactionTypes',
            'incidentTransactionStatuses',
            'category',
            'tag',
            'organization',
            'organizationStatus',
            'enabled',
            'credentialDeliveryStatus',
            'dateRange',
            'deletedDateRange',
            'reportDate',
            'amountRange',
            'accountBalanceRange',
            'notificationType',
            'separatedDateRange',
            'contactDateTimeRange',
            'victimAppealDateTimeRange',
            'answerAuthor',
            'questionAuthor',
            'type',
            'eventType',
        ];
        this.filterFields.forEach(item => {
            if (simpleControlNames.includes(item.name)) {
                this.form.addControl(item.name, new FormControl(null));
            } else if (groupControlNames.includes(item.name)) {
                this.form.addControl(item.name, this.formBuilder.group({}));
            }
        });
        if (this.chips) {
            const group: FormGroup<DynamicBooleanForm> = this.formBuilder.group({});
            this.chips.forEach(chip => group.addControl(chip, new FormControl(false)));
            this.form.removeControl(this.chipsFormControlName);
            this.form.addControl(this.chipsFormControlName, group);
        }
        this.subscribeToFormChanges();
    }

    private updateFilterToggleState(result: FilterEvent): void {
        if (!result) {
            return;
        }
        const booleanFields: (keyof FilterEventForm)[] = [
            'status', 'subjectType', 'creationMethod', 'sourceOrganizations',
            'incidentFraudTypes', 'incidentTransactionTypes', 'incidentTransactionStatuses',
            'category', 'tag', 'organization', 'enabled', 'credentialDeliveryStatus', 'incidentRelatedOrganizations', 'incidentGovernment',
            'notificationType', 'notificationStatus', 'incidentPriority',
            'incidentResolution', 'incidentStatus', 'refundStatus', 'refundType', 'refundAuthor', 'incidentType',
            'contactTypes', 'msisdnStatuses', 'questionAuthor', 'answerAuthor', 'type',
            'transactionTypes', 'transactionStatuses', 'senderBank', 'receiverBank',
            'organizationType', 'organizationStatus', 'creationReason', 'refundRelatedOrganizations', 'refundGovernment',
            'trendStatus', 'trendType', 'fraudReportStatus', 'country', 'region', 'sourceType', 'eventType',
        ];

        this.isFilterSelected =
            !!Object.keys(result)
                .filter(fieldName => fieldName !== this.chipsFormControlName)
                .filter(filedName => booleanFields.includes(filedName as keyof FilterEventForm))
                .filter(fieldName => !!(result as any)[fieldName])
                .map(filedName => (result as any)[filedName])
                .filter(fieldValue => Object.keys(fieldValue)
                    .filter(fieldValueKey => !!(fieldValue as any)[fieldValueKey]).length > 0)
                ?.length
            || hasDateRange(result.dateRange)
            || hasDateRange(result.deletedDateRange)
            || !!result.reportDate?.date
            || hasSeparatedDateRange(result.separatedDateRange)
            || hasAmountRange(result.amountRange)
            || hasAccountBalanceRange(result.accountBalanceRange)
            || hasSeparatedDateRange(result.contactDateTimeRange)
            || hasSeparatedDateRange(result.victimAppealDateTimeRange);
    }
}

export function getFormattedDateOrNull(date?: Date, dateFormat: string = DATE_SERVER_FORMAT): string {
    return date && dayjs(date).format(dateFormat);
}

const hasDateRange = (dateRange?: {dateFrom?: Date; dateTo?: Date}): boolean =>
    !!dateRange?.dateFrom || !!dateRange?.dateTo;

const hasSeparatedDateRange = (dateRange?: FilterDateTimeRangeValue): boolean =>
    !!dateRange?.startDate?.date || !!dateRange?.endDate?.date;

const hasAmountRange = (amountRange?: AmountRangeValue): boolean =>
    (amountRange?.from !== null && amountRange?.from !== undefined) ||
    (amountRange?.to !== null && amountRange?.to !== undefined) ||
    (amountRange?.currency !== null && amountRange?.currency !== undefined);

const hasAccountBalanceRange = (accountBalanceRange?: AccountBalanceValue): boolean =>
    (accountBalanceRange?.from !== null && accountBalanceRange?.from !== undefined) ||
    (accountBalanceRange?.to !== null && accountBalanceRange?.to !== undefined) ||
    (accountBalanceRange?.currency !== null && accountBalanceRange?.currency !== undefined);

export function fieldExtractor(field?: Record<string, boolean>): string[] {
    return field && Object.keys(field).filter(key => !key.includes('search')).filter(key => field[key]);
}

export interface FilterSelectField<T> extends FilterField {
    isMultiSelect?: boolean;
    values: T[],
}

export interface FilterDictionaryField extends FilterSelectField<ReferenceBookResponse> {
    dictionaryName: string,
    filterBy?: string[];
}

export interface FilterAsyncField extends FilterSelectField<AsyncFieldViewModel> {
    request: Observable<AsyncFieldViewModel[]>
}

export interface FilterField {
    name: keyof FilterEventForm;
    type: FilterFieldType;
    translationPrefix?: string;
}

export declare type FilterFieldType = 'text' | 'select' | 'date' | 'dateRange' | 'amountRange' | 'accountBalanceRange';
export declare type SearchPlaceholderContext =
    'person_and_organizations'
    | 'list'
    | 'loans'
    | 'users'
    | 'incidents'
    | 'notifications'
    | 'incidentRefunds'
    | 'transactions'
    | 'refunds'
    | 'contactDetails'
    | 'msisdn'
    | 'msisdnFraudEntries'
    | 'faq'
    | 'knowledgeDocuments'
    | 'af_centre'
    | 'organizations'
    | 'organizationDocuments'
    | 'analyticAgentReports'
    | 'trends'
    | 'trendIncidents'
    | 'trendFeedbacks'
    | 'trendStatistics'
    | 'fraudReports'
    | 'fraudReportData'
    | 'fraudReportFiles'
    | 'history';

export declare type FilterEvent = ɵTypedOrUntyped<FilterEventForm, ɵFormGroupValue<FilterEventForm>, any>;
export declare type FilterFormValue = ɵTypedOrUntyped<FilterEventForm, ɵFormGroupRawValue<FilterEventForm>, any>;

export interface FilterEventForm {
    status?: FormGroup<DynamicBooleanForm>;
    subjectType?: FormGroup<DynamicBooleanForm>,
    incidentType?: FormGroup<DynamicBooleanForm>;
    creationMethod?: FormGroup<DynamicBooleanForm>,
    creationReason?: FormGroup<DynamicBooleanForm>,
    organizationType?: FormGroup<DynamicBooleanForm>,
    organizationStatus?: FormGroup<DynamicBooleanForm>,
    organization?: FormGroup<DynamicBooleanForm>;
    credentialDeliveryStatus?: FormGroup<DynamicBooleanForm>;
    enabled?: FormGroup<DynamicBooleanForm>;
    sourceOrganizations?: FormGroup<DynamicBooleanForm>;
    incidentFraudTypes?: FormGroup<DynamicBooleanForm>;
    incidentTransactionTypes?: FormGroup<DynamicBooleanForm>;
    incidentTransactionStatuses?: FormGroup<DynamicBooleanForm>;
    category?: FormGroup<DynamicBooleanForm>;
    tag?: FormGroup<DynamicBooleanForm>;
    dateRange?: FormGroup<DateRangeForm>;
    deletedDateRange?: FormGroup<DateRangeForm>;
    reportDate?: FormGroup<DateForm>;
    separatedDateRange?: FormGroup<FilterDateTimeRange>;
    amountRange?: FormGroup<AmountRange>;
    accountBalanceRange?: FormGroup<AccountBalanceRange>;
    incidentRelatedOrganizations?: FormGroup<DynamicBooleanForm>;
    incidentGovernment?: FormGroup<DynamicBooleanForm>;
    query?: FormControl<string>;
    rrn?: FormControl<string>;
    notificationType?: FormGroup<DynamicBooleanForm>;
    notificationStatus?: FormGroup<DynamicBooleanForm>;
    incidentPriority?: FormGroup<DynamicBooleanForm>;
    incidentResolution?: FormGroup<DynamicBooleanForm>;
    incidentStatus?: FormGroup<DynamicBooleanForm>;
    refundStatus?: FormGroup<DynamicBooleanForm>;
    deleted?: FormControl<boolean>;
    incidentRole?: FormControl<IncidentFilterRole>;
    incidentSourceAuthors?: FormControl<string[]>;
    refundType?: FormGroup<DynamicBooleanForm>;
    refundAuthor?: FormControl<string>;
    refundCreatedByUsername?: FormControl<string>;
    refundRelatedOrganizations?: FormGroup<DynamicBooleanForm>;
    refundGovernment?: FormGroup<DynamicBooleanForm>;
    refundOrganizations?: FormControl<string[]>;
    refundExcludeAuthorOrganizations?: FormControl<string[]>;
    transactionStatuses?: FormGroup<DynamicBooleanForm>;
    transactionTypes?: FormGroup<DynamicBooleanForm>;
    senderBank?: FormGroup<DynamicBooleanForm>;
    receiverBank?: FormGroup<DynamicBooleanForm>;
    contactTypes?: FormGroup<DynamicBooleanForm>;
    msisdnStatuses?: FormGroup<DynamicBooleanForm>;
    contactDateTimeRange?: FormGroup<FilterDateTimeRange>;
    victimAppealDateTimeRange?: FormGroup<FilterDateTimeRange>;
    questionAuthor?: FormGroup<DynamicBooleanForm>;
    answerAuthor?: FormGroup<DynamicBooleanForm>;
    type?: FormGroup<DynamicBooleanForm>;
    eventType?: FormGroup<DynamicBooleanForm>;
    trendStatus?: FormGroup<DynamicBooleanForm>;
    trendType?: FormGroup<DynamicBooleanForm>;
    country?: FormGroup<DynamicBooleanForm>;
    region?: FormGroup<DynamicBooleanForm>;
    sourceType?: FormGroup<DynamicBooleanForm>;
    trendDetectedDateRange?: FormGroup<DateRangeForm>;
    trendUpdatedDateRange?: FormGroup<FilterDateTimeRange>;
    fraudReportDateRange?: FormGroup<DateRangeForm>;
    fraudReportStatus?: FormGroup<DynamicBooleanForm>;
    fraudReportUpdatedDateRange?: FormGroup<FilterDateTimeRange>;
    fraudReportSentDateRange?: FormGroup<FilterDateTimeRange>;
}

export declare type FilterDateTimeRangeValue = ɵTypedOrUntyped<FilterDateTimeRange, ɵFormGroupValue<FilterDateTimeRange>, any>;

export interface FilterDateTimeRange {
    startDate?: FormGroup<DateForm>;
    endDate?: FormGroup<DateForm>;
}

export interface DynamicBooleanForm {
    [key: string]: FormControl<boolean>;
}

export declare type AmountRangeValue = ɵTypedOrUntyped<AmountRange, ɵFormGroupValue<AmountRange>, any>;

export interface AmountRange {
    from?: FormControl<number>;
    to?: FormControl<number>;
    currency?: FormControl<string>;
}

export declare type AccountBalanceValue = ɵTypedOrUntyped<AccountBalanceRange, ɵFormGroupValue<AccountBalanceRange>, any>;

export interface AccountBalanceRange {
    from?: FormControl<number>;
    to?: FormControl<number>;
    currency?: FormControl<string>;
}

export interface AsyncFieldViewModel {
    code: string;
    name_ru: string;
}
